import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Pencil, Check, Link as LinkIcon, Plus, Trash2, ChevronUp, ChevronDown, Code, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ReferenceList, ProcessingRule } from '@/entities/all';
import ElementVersionForm from '@/components/elements/ElementVersionForm';
import ViewProcessingRuleModal from '@/components/processing-rules/ViewProcessingRuleModal';

/**
 * Editable table for imported elements.
 * Shows all fields from the imported file. Clicking edit opens a modal
 * with Basic Info + Processing tabs. All edits are local only — no DB writes.
 */
export const ImportEditTable = forwardRef(function ImportEditTable({ items = [], onChange }, ref) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [referenceLists, setReferenceLists] = useState([]);
  const [editRuleNames, setEditRuleNames] = useState([]);
  const [selectedRuleName, setSelectedRuleName] = useState('');
  const [availableRules, setAvailableRules] = useState([]);
  const [viewingRule, setViewingRule] = useState(null);
  const [ruleComboboxOpen, setRuleComboboxOpen] = useState(false);
  const [ruleSearch, setRuleSearch] = useState('');

  useEffect(() => {
    ReferenceList.list().then(setReferenceLists).catch(() => {});
    ProcessingRule.filter({ status: 'Active' }, 'rule_name').then(setAvailableRules).catch(() => {});
  }, []);

  useImperativeHandle(ref, () => ({
    getItems: () => items
  }));

  const parseRuleNames = (processingRules) => {
    if (!processingRules) return [];
    if (Array.isArray(processingRules)) return [...processingRules];
    if (typeof processingRules === 'string' && processingRules.trim()) {
      return processingRules.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  const openEdit = (index) => {
    const item = items[index];
    setEditFormData({
      canonical_name: item.canonical_name || '',
      definition: item.definition || '',
      data_type: item.data_type || 'string',
      completeness_weight: item.completeness_weight ?? 1,
      timeliness_weight: item.timeliness_weight ?? 1,
      status: item.status || 'DRAFT',
      reference_list_name: item.reference_list_name || null,
      confidence_score: item.confidence_score || 0.95,
      business_summary: item.business_summary || '',
      processing_language: item.processing_language || 'python'
    });
    setEditRuleNames(parseRuleNames(item.processing_rules));
    setSelectedRuleName('');
    setEditingIndex(index);
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    const updated = [...items];
    updated[editingIndex] = {
      ...updated[editingIndex],
      ...editFormData,
      processing_rules: editRuleNames,
      _edited: true
    };
    onChange(updated);
    setEditingIndex(null);
    setEditFormData({});
    setEditRuleNames([]);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditFormData({});
    setEditRuleNames([]);
  };

  const handleFormChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const getRuleTypeColor = (ruleType) => {
    const colorMap = {
      'validation': 'bg-blue-100 text-blue-800',
      'transformation': 'bg-purple-100 text-purple-800',
      'normalization': 'bg-green-100 text-green-800',
      'enrichment': 'bg-orange-100 text-orange-800'
    };
    return colorMap[ruleType] || 'bg-gray-100 text-gray-800';
  };

  // Local rule name list manipulation
  const addRuleName = () => {
    if (!selectedRuleName) return;
    if (editRuleNames.includes(selectedRuleName)) return;
    setEditRuleNames(prev => [...prev, selectedRuleName]);
    setSelectedRuleName('');
  };

  const removeRuleName = (index) => {
    setEditRuleNames(prev => prev.filter((_, i) => i !== index));
  };

  const moveRuleName = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= editRuleNames.length) return;
    setEditRuleNames(prev => {
      const arr = [...prev];
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr;
    });
  };

  if (items.length === 0) {
    return <p className="text-center text-muted-foreground py-4">No elements in file</p>;
  }

  const editingItem = editingIndex !== null ? items[editingIndex] : null;

  return (
    <>
      <Card>
        <CardContent className="pt-4 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[30px]"></TableHead>
                <TableHead className="min-w-[180px]">Canonical Name</TableHead>
                <TableHead className="min-w-[200px]">Definition</TableHead>
                <TableHead className="w-[100px]">Data Type</TableHead>
                <TableHead className="w-[80px]">Status</TableHead>
                <TableHead className="min-w-[150px]">Business Summary</TableHead>
                <TableHead className="w-[130px]">Reference List</TableHead>
                <TableHead className="min-w-[130px]">Processing Rules</TableHead>
                <TableHead className="w-[100px]">Language</TableHead>
                <TableHead className="w-[60px]">Version</TableHead>
                <TableHead className="w-[30px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => {
                const ruleNames = parseRuleNames(item.processing_rules);

                return (
                  <TableRow key={index} className={item._edited ? 'bg-yellow-50' : ''}>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEdit(index)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </TableCell>
                    <TableCell className="font-medium">{item.canonical_name}</TableCell>
                    <TableCell>
                      <span className="text-sm max-w-[200px] truncate block" title={item.definition}>
                        {item.definition || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.data_type || 'string'}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.status || 'DRAFT'}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm max-w-[150px] truncate block" title={item.business_summary}>
                        {item.business_summary || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{item.reference_list_name || '-'}</span>
                    </TableCell>
                    <TableCell>
                      {ruleNames.length > 0 ? (
                        <span className="text-sm" title={ruleNames.join(', ')}>
                          {ruleNames.length} rule{ruleNames.length !== 1 ? 's' : ''}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{item.processing_language || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{item.version_number || '-'}</span>
                    </TableCell>
                    <TableCell>
                      {item._edited && <Check className="h-3 w-3 text-green-600" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={editingIndex !== null} onOpenChange={(open) => { if (!open) cancelEdit(); }}>
        <DialogContent className="!max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit: {editingItem?.canonical_name}</DialogTitle>
            <DialogDescription>
              Modify the properties for this element before importing.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="processing">Processing</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 pt-4">
              <div>
                <Label htmlFor="import_canonical_name">Canonical Name</Label>
                <Input
                  id="import_canonical_name"
                  value={editFormData.canonical_name || ''}
                  onChange={(e) => handleFormChange('canonical_name', e.target.value)}

                />
              </div>
              <ElementVersionForm
                formData={editFormData}
                onChange={handleFormChange}
                disabled={false}
                referenceLists={referenceLists}
                showChangeDescription={false}
                useReferenceListName={true}
              />

              <div>
                <Label htmlFor="import_business_summary">Business Summary</Label>
                <Textarea
                  id="import_business_summary"
                  value={editFormData.business_summary || ''}
                  onChange={(e) => handleFormChange('business_summary', e.target.value)}
                  placeholder="Business summary..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="import_processing_language">Processing Language</Label>
                  <Select
                    value={editFormData.processing_language || 'python'}
                    onValueChange={(v) => handleFormChange('processing_language', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="python">Python</SelectItem>
                      <SelectItem value="sql">SQL</SelectItem>
                      <SelectItem value="javascript">JavaScript</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="processing" className="space-y-6 pt-4">
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                <div className="flex items-center gap-2 mb-3">
                  <LinkIcon className="w-4 h-4 text-blue-600" />
                  <Label className="font-semibold">Processing Rules</Label>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Processing rule names to link during import. Rules execute in order from top to bottom.
                </p>

                {/* Add Rule */}
                <div className="flex gap-2 mb-4">
                  <Popover open={ruleComboboxOpen} onOpenChange={(open) => { setRuleComboboxOpen(open); if (!open) setRuleSearch(''); }}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" aria-expanded={ruleComboboxOpen} className="flex-1 justify-between font-normal">
                        {selectedRuleName || "Search rules..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <div className="flex items-center border-b px-3">
                        <input
                          className="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                          placeholder="Search processing rules..."
                          value={ruleSearch}
                          onChange={(e) => setRuleSearch(e.target.value)}
                        />
                      </div>
                      <div className="max-h-[200px] overflow-y-auto p-1">
                        {(() => {
                          const filtered = availableRules
                            .filter(r => !editRuleNames.includes(r.rule_name))
                            .filter(r => !ruleSearch || r.rule_name.toLowerCase().includes(ruleSearch.toLowerCase()));
                          if (filtered.length === 0) {
                            return <div className="py-6 text-center text-sm text-muted-foreground">No rules found.</div>;
                          }
                          return filtered.map(rule => (
                            <div
                              key={rule.id}
                              className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                              onClick={() => {
                                setSelectedRuleName(rule.rule_name);
                                setRuleComboboxOpen(false);
                                setRuleSearch('');
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", selectedRuleName === rule.rule_name ? "opacity-100" : "opacity-0")} />
                              <span>{rule.rule_name}</span>
                              <Badge className={`${getRuleTypeColor(rule.rule_type)} text-xs ml-2`}>
                                {rule.rule_type}
                              </Badge>
                            </div>
                          ));
                        })()}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const rule = availableRules.find(r => r.rule_name === selectedRuleName);
                      if (rule) setViewingRule(rule);
                    }}
                    disabled={!selectedRuleName}
                    size="sm"
                    title="Preview rule code"
                  >
                    <Code className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    onClick={addRuleName}
                    disabled={!selectedRuleName}
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>

                {/* Rule Names List */}
                <div className="space-y-2">
                  {editRuleNames.length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="py-6 text-center text-slate-500 text-sm">
                        No processing rules. Add rule names above.
                      </CardContent>
                    </Card>
                  ) : (
                    editRuleNames.map((name, index) => {
                      const rule = availableRules.find(r => r.rule_name === name);
                      return (
                        <Card key={`${name}-${index}`} className={`border ${!rule ? 'border-l-4 border-l-yellow-400' : 'border-slate-200'}`}>
                          <CardContent className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="flex flex-col items-center text-slate-400 w-6">
                                <span className="text-xs font-mono">{index + 1}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-900">{name}</span>
                                  {rule ? (
                                    <Badge className={`${getRuleTypeColor(rule.rule_type)} text-xs`}>
                                      {rule.rule_type}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-300">
                                      Not found in repository
                                    </Badge>
                                  )}
                                </div>
                                {rule?.description && (
                                  <p className="text-xs text-slate-500 truncate mt-0.5">{rule.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                {rule && (
                                  <Button
                                    type="button" variant="ghost" size="sm"
                                    onClick={() => setViewingRule(rule)}
                                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                                    title="View rule code"
                                  >
                                    <Code className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  type="button" variant="ghost" size="sm"
                                  onClick={() => moveRuleName(index, 'up')}
                                  disabled={index === 0}
                                  className="h-8 w-8 p-0"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button" variant="ghost" size="sm"
                                  onClick={() => moveRuleName(index, 'down')}
                                  disabled={index === editRuleNames.length - 1}
                                  className="h-8 w-8 p-0"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button" variant="ghost" size="sm"
                                  onClick={() => removeRuleName(index)}
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>

                {editRuleNames.length > 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    {editRuleNames.length} rule{editRuleNames.length !== 1 ? 's' : ''} will be linked during import.
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
            <Button onClick={saveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ViewProcessingRuleModal
        isOpen={!!viewingRule}
        onClose={() => setViewingRule(null)}
        rule={viewingRule}
      />
    </>
  );
});
